/**
 * Meta Pixel helpers.
 *
 * The pixel itself is injected by src/lib/consent.ts after a marketing opt-in
 * (it fires PageView on load). This module only adds conversion events on top:
 * InitiateCheckout, Purchase, Lead (free preview ordered) and
 * CompleteRegistration. The last three also go server-side via the
 * Conversions API with a shared event id.
 *
 * Purchase is sent from BOTH the browser (here) and the server (the Conversions
 * API call in supabase/functions/stripe-webhook). They share a deterministic
 * `eventID` derived from the Stripe session, which is how Meta deduplicates the
 * pair — change the id on one side only and every purchase counts twice.
 */

import { hasConsent } from "@/lib/consent";
import { supabase } from "@/integrations/supabase/client";

/**
 * Fire a standard pixel event. No-ops during SSR, without marketing consent,
 * and when the pixel hasn't loaded (ad blocker, or the injection failed).
 */
export const trackMetaPixel = (
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string,
) => {
  if (typeof window === "undefined" || !hasConsent("marketing")) return;
  if (typeof window.fbq !== "function") return;
  try {
    window.fbq("track", eventName, params, eventId ? { eventID: eventId } : undefined);
  } catch (error) {
    console.warn("Meta pixel event failed:", error);
  }
};

const readCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
};

/**
 * The pixel's browser identifiers: `_fbp` (browser id) and `_fbc` (ad click id,
 * set when the visitor arrived with an `fbclid`). Passed to create-payment so
 * they can ride along on the Stripe session and reach the Conversions API —
 * they are the strongest matching signals Meta has short of an email.
 *
 * Returns nothing without marketing consent. The Conversions API is the same
 * advertising processing as the pixel, only server-to-server, so consent gates
 * it identically; stale cookies from a withdrawn consent must not leak through.
 */
export const getMetaBrowserIds = (): { fbp?: string; fbc?: string } => {
  if (!hasConsent("marketing")) return {};
  return { fbp: readCookie("_fbp"), fbc: readCookie("_fbc") };
};

/** Marketing-consent flag, forwarded so the server can gate its own CAPI call. */
export const hasMarketingConsent = (): boolean => hasConsent("marketing");

/**
 * Must match `purchaseEventId` in supabase/functions/_shared/meta-capi.ts.
 * Derived from the Stripe session id so both sides compute it independently.
 */
export const purchaseEventId = (stripeSessionId: string): string =>
  `purchase_${stripeSessionId}`;

/**
 * Purchase details stashed at checkout so the success page can report an
 * accurate value. Guests can't read it back from `payment-status` (that endpoint
 * requires auth and their account is created only after payment), so without
 * this their browser-side Purchase would have no value attached.
 */
const PURCHASE_CONTEXT_KEY = "metaPurchaseContext";

export interface MetaPurchaseContext {
  amountCents: number;
  currency: string;
  credits: number;
}

export const stashPurchaseContext = (context: MetaPurchaseContext) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(PURCHASE_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // Private mode / storage full — the server-side event still has the value.
  }
};

export const takePurchaseContext = (): MetaPurchaseContext | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PURCHASE_CONTEXT_KEY);
    if (!raw) return null;
    // Consume it so a refresh of the success page can't fire a second Purchase.
    sessionStorage.removeItem(PURCHASE_CONTEXT_KEY);
    return JSON.parse(raw) as MetaPurchaseContext;
  } catch {
    return null;
  }
};

/** Must match `leadEventId` in supabase/functions/_shared/meta-capi.ts. */
export const leadEventId = (orderId: string): string => `lead_${orderId}`;

/** Must match `registrationEventId` in supabase/functions/_shared/meta-capi.ts. */
export const registrationEventId = (userId: string): string => `registration_${userId}`;

/**
 * What a server-side Conversions API call needs from the browser: the consent
 * decision (the server sends nothing without "granted"), the pixel cookies and
 * the page. Spread into the body of an edge-function call that reports an event.
 */
export const getMetaCapiContext = () => ({
  metaConsent: hasMarketingConsent() ? "granted" : "denied",
  ...getMetaBrowserIds(),
  eventSourceUrl: typeof window === "undefined" ? undefined : window.location.href,
});

/**
 * Free preview ordered — the funnel's "Lead". order-report sends the server-side
 * twin with the same id, so Meta counts it once.
 */
export const trackFreeReportLead = (orderId: string) => {
  trackMetaPixel("Lead", { content_name: "free_preview_report" }, leadEventId(orderId));
};

const REGISTRATION_TRACKED_KEY = "metaRegistrationTracked";

/**
 * Account created — CompleteRegistration, from the pixel and (via track-signup)
 * the Conversions API with one shared id. Called from both the sign-up form and
 * the first OAuth sign-in; a per-browser flag keeps it to one call per account,
 * and the shared id makes a second device's call a duplicate Meta drops.
 */
export const trackRegistration = (userId: string, method: string) => {
  if (typeof window === "undefined" || !hasConsent("marketing")) return;
  try {
    if (localStorage.getItem(REGISTRATION_TRACKED_KEY) === userId) return;
    localStorage.setItem(REGISTRATION_TRACKED_KEY, userId);
  } catch {
    // Storage blocked: at worst the event is sent twice with one id.
  }

  trackMetaPixel("CompleteRegistration", { registration_method: method }, registrationEventId(userId));
  void supabase.functions
    .invoke("track-signup", { body: { userId, ...getMetaCapiContext() } })
    .catch(() => {});
};
